/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../buttons/Button";
import { errorAlert, makeApiRequest, successAlert } from "../../util";
import AuthContext from "../../store/authContext";
import { ADD_OTAR_CONNECTION_FORM_FIELDS } from "../../constants";
import Form from "../../ui/Form/Form";
import ToggleField from "../inputs/ToggleField";
import NumberField from "../inputs/NumberField";
import TextField from "../inputs/TextField";
import { IOTARConnectionData } from "../../types";
import DeleteModal, { DeleteModalIcons } from "../modal/DeleteModal";
import {
  CREATE_OTAR_CONNECTION_TRANSLATIONS,
  FORM_VALIDATION_TRANSLATIONS,
} from "../../translations/util";

interface IProps {
  org: string;
}
const set = new Set();

const isInValidNumberForField = (val?: number, min?: number, max?: number) => {
  if (val || val === 0) {
    return val < (min || 0) || val > (max || 1048575);
  }

  return true;
};

const isFormInvalid = ({ data }: { data: any }) => {
  if (!data) return true;

  const isConnectionFormInvalid = ADD_OTAR_CONNECTION_FORM_FIELDS.map(
    (rows: any) => {
      const res = rows.fields.map((field: any) => {
        switch (field.type) {
          case "TEXT":
            return field.isInputInvalid?.(data[field.name]);
          case "NUMBER":
            return isInValidNumberForField(
              data[field.name],
              field.min,
              field.max
            );
          default:
            return false;
        }
      });

      return res.reduce((acc: any, el: any) => acc || el);
    }
  ).reduce((acc: any, el: any) => acc || el);

  return isConnectionFormInvalid;
};

const NewOTARConnection = ({ org }: IProps) => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    set.clear();
    return () => {
      set.clear();
    };
  }, []);

  const [connectionData, setConnectionData] = useState<IOTARConnectionData>({
    "CONNECTION-ID": undefined,
    "KMF-IPV4-ADDR": undefined,
    "KMF-PORT": undefined,
    "KMF-RADIO-SET-IDENTIFIER": undefined,
    "IWF-RADIO-SET-IDENTIFIER": undefined,
    "MESSAGE-NUMBER-PERIOD": undefined,
    "PRIVATE-CALL-SLN": undefined,
    "GROUP-REGROUP-SLN": undefined,
    "OTAR-REGISTRATION-TIMER": 8,
    "OPT-SEND-REKEY-REQ": false,
    "OPT-REVERSE-WARM-START": false,
    "OPT-DE-REGISTRATION": false,
    "OPT-HELLO-MAC-REPLY": false,
    "OPT-KEY-ASSIGNMENT": false,
    "KEEP-ALIVE-TIMER": 0,
  });

  const createOTARConnection = async () => {
    setLoading(true);
    try {
      await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/app-config/otar-connections`,
        method: "POST",
        body: JSON.stringify({
          "CONNECTION-ID": connectionData["CONNECTION-ID"],
          "KMF-IPV4-ADDR": connectionData["KMF-IPV4-ADDR"],
          "KMF-PORT": connectionData["KMF-PORT"],
          "KMF-RADIO-SET-IDENTIFIER":
            connectionData["KMF-RADIO-SET-IDENTIFIER"],
          "IWF-RADIO-SET-IDENTIFIER":
            connectionData["IWF-RADIO-SET-IDENTIFIER"],
          "MESSAGE-NUMBER-PERIOD": connectionData["MESSAGE-NUMBER-PERIOD"],
          "PRIVATE-CALL-SLN": connectionData["PRIVATE-CALL-SLN"],
          "GROUP-REGROUP-SLN": connectionData["GROUP-REGROUP-SLN"],
          "OTAR-REGISTRATION-TIMER": connectionData["OTAR-REGISTRATION-TIMER"],
          "OPT-SEND-REKEY-REQ": connectionData["OPT-SEND-REKEY-REQ"],
          "OPT-REVERSE-WARM-START": connectionData["OPT-REVERSE-WARM-START"],
          "OPT-DE-REGISTRATION": connectionData["OPT-DE-REGISTRATION"],
          "OPT-HELLO-MAC-REPLY": connectionData["OPT-HELLO-MAC-REPLY"],
          "OPT-KEY-ASSIGNMENT": connectionData["OPT-KEY-ASSIGNMENT"],
          "KEEP-ALIVE-TIMER": connectionData["KEEP-ALIVE-TIMER"],
        }),
        token,
        org,
      });
      successAlert(CREATE_OTAR_CONNECTION_TRANSLATIONS.successAlertMessage);
      navigate("/configurationManagement/otarConnections");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="data-form">
        <Form>
          <div className="data-tabcontent data-card">
            {ADD_OTAR_CONNECTION_FORM_FIELDS.map(
              (rows: {
                cols: number;
                fields: {
                  name: keyof IOTARConnectionData;
                  tooltipText?: string;
                  label: string;
                  type: string;
                  invalidText?: string | null;
                  isInputInvalid?: (val?: string) => boolean;
                  min?: number;
                  max?: number;
                }[];
              }) => {
                return (
                  <div
                    className={`grid grid-cols-${rows.cols} gap-4 form-control`}
                  >
                    {rows.fields.map((field) => {
                      switch (field.type) {
                        case "TOGGLE":
                          return (
                            <ToggleField
                              labelText={field.label}
                              onChange={(val) =>
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]: val,
                                })
                              }
                              value={connectionData[field.name] as boolean}
                              tooltipText={field.tooltipText}
                              defaultToggled
                            />
                          );
                        case "NUMBER":
                          return (
                            <NumberField
                              onChange={(val) => {
                                set.add(field.name);
                                if (!val || !Number.isNaN(+val))
                                  setConnectionData({
                                    ...connectionData,
                                    [field.name]: val ? +val : undefined,
                                  });
                              }}
                              label={field.label}
                              placeholder={field.label}
                              max={field.max || 0}
                              min={field.min || 0}
                              invalid={
                                set.has(field.name) &&
                                connectionData[field.name] !== 0 &&
                                !connectionData[field.name]
                              }
                              value={connectionData[field.name] as number}
                              invalidText={`"${field.label}" ${FORM_VALIDATION_TRANSLATIONS.range} (${field.min}-${field.max})`}
                              tooltipText={field.tooltipText}
                              required
                            />
                          );
                        default:
                          return (
                            <TextField
                              onChange={(val) => {
                                set.add(field.name);
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]: val,
                                });
                              }}
                              placeholder={field.label}
                              value={connectionData[field.name] as string}
                              invalid={
                                set.has(field.name) &&
                                field.isInputInvalid?.(
                                  connectionData[field.name] as string
                                )
                              }
                              invalidText={
                                field.invalidText ||
                                FORM_VALIDATION_TRANSLATIONS.required(
                                  field.label
                                )
                              }
                              labelText={field.label}
                              tooltipText={field.tooltipText}
                              required
                            />
                          );
                      }
                    })}
                  </div>
                );
              }
            )}
          </div>
        </Form>
      </div>
      <DeleteModal
        modalHeaderText={CREATE_OTAR_CONNECTION_TRANSLATIONS.deleteModal.header}
        modalDescriptionText={
          CREATE_OTAR_CONNECTION_TRANSLATIONS.deleteModal.description
        }
        onDelete={() => navigate("/configurationManagement/otarConnections")}
        open={open}
        setOpen={setOpen}
        confirmationButtonLabel={
          CREATE_OTAR_CONNECTION_TRANSLATIONS.deleteModal.goBackButtonLabel
        }
        cancelIcon={DeleteModalIcons.Cancel}
      />
      <div className="flex justify-end form-footer">
        <div className="mr-2">
          <Button
            className="w-full"
            type="button"
            kind="tertiary"
            disabled={false}
            label={CREATE_OTAR_CONNECTION_TRANSLATIONS.cancelButtonLabel}
            onClick={() => setOpen(true)}
            dataCy="cancelCreateOtarConnection-"
          />
        </div>

        <div>
          <Button
            className="w-full"
            type="button"
            loading={loading}
            disabled={isFormInvalid({ data: connectionData })}
            onClick={createOTARConnection}
            label={CREATE_OTAR_CONNECTION_TRANSLATIONS.createButtonLabel}
            dataCy="createOtarConnection-"
          />
        </div>
      </div>
    </>
  );
};

export default NewOTARConnection;
